// useCalendarData.js - Hook personalizado actualizado para integrar datos médicos con el calendario

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import calendarDataService from '../../../components/PatientTreatmentDetails/services/calendarDataService';

/**
 * Hook personalizado para manejar los datos del calendario médico
 * @param {string} patientId - ID del paciente
 * @param {Object} options - Opciones de configuración
 * @returns {Object} Estado y funciones para el calendario
 */
export const useCalendarData = (patientId, options = {}) => {
  // Estados principales
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Referencias para control de requests
  const abortControllerRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // Opciones por defecto mejoradas
  const config = {
    autoRefresh: true,
    refreshInterval: 30000, // 30 segundos
    enableCache: true,
    maxCacheAge: 300000, // 5 minutos
    enableProgressTracking: true,
    retryAttempts: 3,
    retryDelay: 1000,
    ...options
  };

  // Cache mejorado con metadatos
  const [cache, setCache] = useState(new Map());

  /**
   * Función para trackear el progreso de carga
   */
  const updateLoadingProgress = useCallback((step, total) => {
    if (config.enableProgressTracking) {
      const progress = Math.round((step / total) * 100);
      setLoadingProgress(progress);
    }
  }, [config.enableProgressTracking]);

  /**
   * Función principal mejorada para cargar todos los eventos médicos
   */
  const loadAllEvents = useCallback(async (forceRefresh = false, retryCount = 0) => {
    
    if (!patientId) {
      console.warn('⚠️ No patientId provided to useCalendarData');
      setEvents([]);
      setLoadingProgress(0);
      return;
    }

    // Cancelar request anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Crear nuevo AbortController
    abortControllerRef.current = new AbortController();

    // Verificar cache si está habilitado
    if (config.enableCache && !forceRefresh) {
      const cacheKey = `medical-events-${patientId}`;
      const cachedData = cache.get(cacheKey);
      
      if (cachedData && Date.now() - cachedData.timestamp < config.maxCacheAge) {
        console.log('📦 Using cached medical events data');
        setEvents(cachedData.events);
        setLastUpdated(new Date(cachedData.timestamp));
        setLoadingProgress(100);
        return cachedData.events;
      }
    }

    setIsLoading(true);
    setError(null);
    setLoadingProgress(0);

    try {
      console.log(`🚀 Loading medical events for patient: ${patientId} (attempt ${retryCount + 1})`);
      
      updateLoadingProgress(1, 7); // Inicio

      // Usar el servicio actualizado
      const allEvents = await calendarDataService.getAllMedicalEvents(patientId);
      
      // Verificar si la request fue cancelada
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      updateLoadingProgress(7, 7); // Completado

      // Actualizar cache con metadatos adicionales
      if (config.enableCache) {
        const cacheKey = `medical-events-${patientId}`;
        const cacheData = {
          events: allEvents,
          timestamp: Date.now(),
          patientId,
          eventCount: allEvents.length,
          statistics: calendarDataService.getEventsStatistics(allEvents)
        };
        
        setCache(prev => new Map(prev).set(cacheKey, cacheData));
      }

      setEvents(allEvents);
      setLastUpdated(new Date());
      setLoadingProgress(100);
      
      console.log(`✅ Successfully loaded ${allEvents.length} medical events`);
      console.log('📊 Event statistics:', calendarDataService.getEventsStatistics(allEvents));
      
      return allEvents;
      
    } catch (err) {
      // No reintentar si la request fue cancelada
      if (err.name === 'AbortError') {
        console.log('🛑 Request cancelled');
        return;
      }

      console.error(`❌ Error loading medical events (attempt ${retryCount + 1}):`, err);
      
      // Lógica de reintentos
      if (retryCount < config.retryAttempts - 1) {
        console.log(`🔄 Retrying in ${config.retryDelay}ms...`);
        setTimeout(() => {
          loadAllEvents(forceRefresh, retryCount + 1);
        }, config.retryDelay * (retryCount + 1)); // Backoff exponencial
        return;
      }

      setError({
        message: err.message,
        code: err.code || 'UNKNOWN_ERROR',
        timestamp: Date.now(),
        retryCount: retryCount + 1
      });
      setEvents([]);
      setLoadingProgress(0);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, config, cache, updateLoadingProgress]);

  /**
   * Cargar eventos al montar el componente o cambiar el patientId
   */
  useEffect(() => {
    if (patientId) {
      loadAllEvents();
    }

    // Cleanup al desmontar
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [patientId, loadAllEvents]);

  /**
   * Auto-refresh mejorado con control de lifecycle
   */
  useEffect(() => {
    if (!config.autoRefresh || !patientId) return;

    const scheduleRefresh = () => {
      refreshTimeoutRef.current = setTimeout(() => {
        console.log('🔄 Auto-refreshing medical events...');
        loadAllEvents(true).then(() => {
          if (config.autoRefresh) {
            scheduleRefresh(); // Programar siguiente refresh
          }
        });
      }, config.refreshInterval);
    };

    scheduleRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [config.autoRefresh, config.refreshInterval, patientId, loadAllEvents]);

  /**
   * Función para refrescar manualmente
   */
  const refreshEvents = useCallback(() => {
    return loadAllEvents(true);
  }, [loadAllEvents]);

  /**
   * Datos agrupados y estadísticas mejoradas usando el servicio
   */
  const eventsByType = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      if (!grouped[event.type]) {
        grouped[event.type] = [];
      }
      grouped[event.type].push(event);
    });
    return grouped;
  }, [events]);

  const eventsByDate = useMemo(() => {
    return calendarDataService.groupEventsByDate(events);
  }, [events]);

  const eventTypeCounts = useMemo(() => {
    return calendarDataService.getEventsSummary(events);
  }, [events]);

  const upcomingEvents = useMemo(() => {
    return calendarDataService.getUpcomingEvents(events, 30); // Próximos 30 días
  }, [events]);

  const overdueEvents = useMemo(() => {
    return calendarDataService.getOverdueEvents(events);
  }, [events]);

  const recentEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter(event => event.date <= now)
      .sort((a, b) => b.date - a.date)
      .slice(0, 10);
  }, [events]);

  const eventsStatistics = useMemo(() => {
    return calendarDataService.getEventsStatistics(events);
  }, [events]);

  /**
   * Funciones de filtrado usando el servicio
   */
  const getEventsInRange = useCallback((startDate, endDate) => {
    return calendarDataService.filterEventsByDateRange(events, startDate, endDate);
  }, [events]);

  const getEventsForDate = useCallback((date) => {
    return calendarDataService.getEventsForDate(events, date);
  }, [events]);

  const getEventsByType = useCallback((type) => {
    return calendarDataService.filterEventsByType(events, type);
  }, [events]);

  const getEventsByTypes = useCallback((types) => {
    return calendarDataService.filterEventsByType(events, types);
  }, [events]);

  /**
   * Función de búsqueda mejorada
   */
  const searchEvents = useCallback((searchTerm, filters = {}) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return filters.types ? getEventsByTypes(filters.types) : events;
    }
    
    const term = searchTerm.toLowerCase();
    let filteredEvents = events.filter(event => 
      event.title.toLowerCase().includes(term) ||
      event.description?.toLowerCase().includes(term) ||
      event.typeLabel.toLowerCase().includes(term) ||
      event.eventSubtype?.toLowerCase().includes(term) ||
      event.doctorName?.toLowerCase().includes(term) ||
      event.status?.toLowerCase().includes(term)
    );

    // Aplicar filtros adicionales
    if (filters.types && filters.types.length > 0) {
      filteredEvents = calendarDataService.filterEventsByType(filteredEvents, filters.types);
    }

    if (filters.status && filters.status.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        filters.status.includes(event.status)
      );
    }

    if (filters.priority && filters.priority.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        filters.priority.includes(event.priority)
      );
    }

    if (filters.dateRange) {
      filteredEvents = calendarDataService.filterEventsByDateRange(
        filteredEvents, 
        filters.dateRange.start, 
        filters.dateRange.end
      );
    }

    return filteredEvents;
  }, [events, getEventsByTypes]);

  /**
   * Estadísticas del calendario mejoradas
   */
  const statistics = useMemo(() => {
    const now = new Date();
    const todayEvents = getEventsForDate(now);
    
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - now.getDay());
    thisWeekStart.setHours(0, 0, 0, 0);
    
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
    thisWeekEnd.setHours(23, 59, 59, 999);
    
    const thisWeekEvents = getEventsInRange(thisWeekStart, thisWeekEnd);
    
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const thisMonthEvents = getEventsInRange(thisMonthStart, thisMonthEnd);

    return {
      ...eventsStatistics,
      today: todayEvents.length,
      thisWeek: thisWeekEvents.length,
      thisMonth: thisMonthEvents.length,
      overdue: overdueEvents.length
    };
  }, [eventsStatistics, getEventsForDate, getEventsInRange, overdueEvents.length]);

  /**
   * Funciones de utilidad adicionales
   */
  const clearCache = useCallback(() => {
    setCache(new Map());
    console.log('🗑️ Calendar data cache cleared');
  }, []);

  const cancelCurrentRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
      setLoadingProgress(0);
      console.log('🛑 Current request cancelled');
    }
  }, []);

  const getCacheInfo = useCallback(() => {
    const cacheKey = `medical-events-${patientId}`;
    const cachedData = cache.get(cacheKey);
    
    if (!cachedData) return null;
    
    return {
      hasCache: true,
      timestamp: cachedData.timestamp,
      age: Date.now() - cachedData.timestamp,
      isStale: Date.now() - cachedData.timestamp > config.maxCacheAge,
      eventCount: cachedData.eventCount,
      statistics: cachedData.statistics
    };
  }, [cache, patientId, config.maxCacheAge]);

  // Retornar el estado y funciones del hook
  return {
    // Estados principales
    events,
    isLoading,
    error,
    lastUpdated,
    loadingProgress,

    // Funciones principales
    refreshEvents,
    loadAllEvents,
    clearCache,
    cancelCurrentRequest,

    // Datos agrupados
    eventsByType,
    eventsByDate,
    upcomingEvents,
    overdueEvents,
    recentEvents,
    statistics,
    eventsStatistics,

    // Funciones de utilidad
    getEventsInRange,
    getEventsForDate,
    getEventsByType,
    getEventsByTypes,
    searchEvents,

    // Metadatos y cache
    hasData: events.length > 0,
    isEmpty: events.length === 0,
    isDataStale: lastUpdated && Date.now() - lastUpdated.getTime() > config.maxCacheAge,
    cacheInfo: getCacheInfo(),

    // Configuración actual
    config: {
      ...config,
      patientId
    }
  };
};

/**
 * Hook especializado para tratamientos
 */
export const useTreatmentCalendarData = (patientId, options = {}) => {
  const calendarData = useCalendarData(patientId, options);
  
  const treatmentEvents = useMemo(() => 
    calendarData.getEventsByType('treatmentDetails'), 
    [calendarData]
  );

  const activeTreatments = useMemo(() => 
    treatmentEvents.filter(event => 
      event.status === 'active' && event.dateType === 'start_date'
    ),
    [treatmentEvents]
  );

  const treatmentPhases = useMemo(() => 
    treatmentEvents.filter(event => event.dateType === 'phase_start'),
    [treatmentEvents]
  );

  return {
    ...calendarData,
    treatments: treatmentEvents,
    activeTreatments,
    treatmentPhases,
    treatmentCount: calendarData.eventTypeCounts.treatmentDetails || 0,
    activeTreatmentCount: activeTreatments.length,
    treatmentStatistics: {
      total: treatmentEvents.length,
      active: activeTreatments.length,
      phases: treatmentPhases.length,
      byStatus: treatmentEvents.reduce((acc, event) => {
        acc[event.status] = (acc[event.status] || 0) + 1;
        return acc;
      }, {})
    }
  };
};

/**
 * Hook especializado para diagnósticos por imagen
 */
export const useDiagnosticCalendarData = (patientId, options = {}) => {
  const calendarData = useCalendarData(patientId, options);
  
  const diagnosticEvents = useMemo(() => 
    calendarData.getEventsByType('diagnosticImaging'), 
    [calendarData]
  );

  const scheduledDiagnostics = useMemo(() => 
    diagnosticEvents.filter(event => 
      event.dateType === 'scheduled_date' && event.status === 'In Progress'
    ),
    [diagnosticEvents]
  );

  const completedDiagnostics = useMemo(() => 
    diagnosticEvents.filter(event => 
      event.dateType === 'completed_date'
    ),
    [diagnosticEvents]
  );

  return {
    ...calendarData,
    diagnostics: diagnosticEvents,
    scheduledDiagnostics,
    completedDiagnostics,
    diagnosticCount: calendarData.eventTypeCounts.diagnosticImaging || 0,
    scheduledCount: scheduledDiagnostics.length,
    completedCount: completedDiagnostics.length,
    diagnosticStatistics: {
      total: diagnosticEvents.length,
      scheduled: scheduledDiagnostics.length,
      completed: completedDiagnostics.length,
      byStudyType: diagnosticEvents.reduce((acc, event) => {
        const type = event.studyType || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {}),
      byBodyPart: diagnosticEvents.reduce((acc, event) => {
        const part = event.bodyPart || 'Unspecified';
        acc[part] = (acc[part] || 0) + 1;
        return acc;
      }, {})
    }
  };
};

/**
 * Hook especializado para órdenes de laboratorio
 */
export const useLaboratoryCalendarData = (patientId, options = {}) => {
  const calendarData = useCalendarData(patientId, options);
  
  const laboratoryEvents = useMemo(() => 
    calendarData.getEventsByType('laboratory'), 
    [calendarData]
  );

  const pendingOrders = useMemo(() => 
    laboratoryEvents.filter(event => 
      event.status === 'Pending' && event.dateType === 'requested_date'
    ),
    [laboratoryEvents]
  );

  const scheduledCollections = useMemo(() => 
    laboratoryEvents.filter(event => 
      event.dateType === 'collection_date' && event.status !== 'Completed'
    ),
    [laboratoryEvents]
  );

  const availableResults = useMemo(() => 
    laboratoryEvents.filter(event => 
      event.dateType === 'result_date' && event.resultStatus === 'Available'
    ),
    [laboratoryEvents]
  );

  return {
    ...calendarData,
    laboratory: laboratoryEvents,
    pendingOrders,
    scheduledCollections,
    availableResults,
    laboratoryCount: calendarData.eventTypeCounts.laboratory || 0,
    pendingCount: pendingOrders.length,
    scheduledCount: scheduledCollections.length,
    resultsAvailable: availableResults.length,
    laboratoryStatistics: {
      total: laboratoryEvents.length,
      pending: pendingOrders.length,
      scheduled: scheduledCollections.length,
      results: availableResults.length,
      byTestType: laboratoryEvents.reduce((acc, event) => {
        const type = event.testType || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {}),
      byPriority: laboratoryEvents.reduce((acc, event) => {
        const priority = event.priority || 'Normal';
        acc[priority] = (acc[priority] || 0) + 1;
        return acc;
      }, {})
    }
  };
};


/** * Hook especializado para notas generales
 */

export const useGeneralNotesCalendarData = (patientId, options = {}) => {
  const calendarData = useCalendarData(patientId, options);
  
  const generalNotesEvents = useMemo(() => 
    calendarData.getEventsByType('generalNotes'),  // ← Cambio de tipo
    [calendarData]
  );

  const activeNotes = useMemo(() => 
    generalNotesEvents.filter(event => 
      event.status === 'active' && event.dateType === 'note_date'
    ),
    [generalNotesEvents]
  );

  const archivedNotes = useMemo(() => 
    generalNotesEvents.filter(event => 
      event.status === 'archived' || event.dateType === 'archived_date'
    ),
    [generalNotesEvents]
  );

  const privateNotes = useMemo(() => 
    generalNotesEvents.filter(event => event.isPrivate),
    [generalNotesEvents]
  );

  return {
    ...calendarData,
    generalNotes: generalNotesEvents,
    activeNotes,
    archivedNotes,
    privateNotes,
    generalNotesCount: calendarData.eventTypeCounts.generalNotes || 0,
    activeNotesCount: activeNotes.length,
    archivedNotesCount: archivedNotes.length,
    privateNotesCount: privateNotes.length,
    notesStatistics: {
      total: generalNotesEvents.length,
      active: activeNotes.length,
      archived: archivedNotes.length,
      private: privateNotes.length,
      byPriority: generalNotesEvents.reduce((acc, event) => {
        const priority = event.priority || 'medium';
        acc[priority] = (acc[priority] || 0) + 1;
        return acc;
      }, {}),
      byTag: generalNotesEvents.reduce((acc, event) => {
        if (event.tags && event.tags.length > 0) {
          event.tags.forEach(tag => {
            acc[tag] = (acc[tag] || 0) + 1;
          });
        }
        return acc;
      }, {})
    }
  };
};

/**
 * Hook especializado para tareas de enfermería
 */
export const useNursingCalendarData = (patientId, options = {}) => {
  const calendarData = useCalendarData(patientId, options);
  
  const nursingEvents = useMemo(() => 
    calendarData.getEventsByType('nursing'), 
    [calendarData]
  );

  const pendingTasks = useMemo(() => 
    nursingEvents.filter(event => 
      event.status === 'Pending' && event.dateType === 'scheduled_date'
    ),
    [nursingEvents]
  );

  const overdueTasks = useMemo(() => {
    const now = new Date();
    return nursingEvents.filter(event => 
      event.dateType === 'scheduled_date' && 
      event.status === 'Pending' && 
      event.date < now
    );
  }, [nursingEvents]);

  const completedTasks = useMemo(() => 
    nursingEvents.filter(event => 
      event.dateType === 'completed_date'
    ),
    [nursingEvents]
  );

  return {
    ...calendarData,
    nursing: nursingEvents,
    pendingTasks,
    overdueTasks,
    completedTasks,
    nursingCount: calendarData.eventTypeCounts.nursing || 0,
    pendingCount: pendingTasks.length,
    overdueCount: overdueTasks.length,
    completedCount: completedTasks.length,
    nursingStatistics: {
      total: nursingEvents.length,
      pending: pendingTasks.length,
      overdue: overdueTasks.length,
      completed: completedTasks.length,
      byTaskType: nursingEvents.reduce((acc, event) => {
        const type = event.taskType || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {}),
      byPriority: nursingEvents.reduce((acc, event) => {
        const priority = event.priority || 'Normal';
        acc[priority] = (acc[priority] || 0) + 1;
        return acc;
      }, {})
    }
  };
};

export default useCalendarData;